<template>
  <div class="p-6 space-y-6">
    <!-- Header Section -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">User Management</h1>
        <p class="text-gray-600 dark:text-gray-400">Manage user accounts, roles, and permissions</p>
      </div>
      <button 
        @click="addUser"
        class="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-500 text-white rounded-lg font-medium shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 transition-all duration-200 hover:scale-105 flex items-center space-x-2"
      >
        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        <span>Add User</span>
      </button>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
      <div class="glass-panel p-6">
        <div class="flex justify-between items-start mb-2">
          <div class="text-gray-500 dark:text-gray-400 text-sm font-medium">Total Users</div>
          <div class="p-2 bg-blue-500/10 rounded-lg">
            <svg class="w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
        </div>
        <div class="text-3xl font-bold text-gray-900 dark:text-white">{{ stats.totalUsers }}</div>
      </div>

      <div class="glass-panel p-6">
        <div class="flex justify-between items-start mb-2">
          <div class="text-gray-500 dark:text-gray-400 text-sm font-medium">Active Users</div>
          <div class="p-2 bg-green-500/10 rounded-lg">
            <svg class="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
        <div class="text-3xl font-bold text-gray-900 dark:text-white">{{ stats.activeUsers }}</div>
      </div>

      <div class="glass-panel p-6">
        <div class="flex justify-between items-start mb-2">
          <div class="text-gray-500 dark:text-gray-400 text-sm font-medium">Admins</div>
          <div class="p-2 bg-purple-500/10 rounded-lg">
            <svg class="w-6 h-6 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
        </div>
        <div class="text-3xl font-bold text-gray-900 dark:text-white">{{ stats.admins }}</div>
      </div>

      <div class="glass-panel p-6">
        <div class="flex justify-between items-start mb-2">
          <div class="text-gray-500 dark:text-gray-400 text-sm font-medium">Pending</div>
          <div class="p-2 bg-orange-500/10 rounded-lg">
            <svg class="w-6 h-6 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
        <div class="text-3xl font-bold text-gray-900 dark:text-white">{{ stats.pending }}</div>
      </div>
    </div>

    <!-- Search and Filters -->
    <div class="glass-panel p-6">
      <div class="flex flex-col md:flex-row gap-4">
        <div class="flex-1 relative">
          <svg class="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input 
            v-model="searchQuery"
            type="text" 
            placeholder="Search users by name, email, or role..." 
            class="glass-input pl-12 w-full"
          />
        </div>
        <select v-model="roleFilter" class="glass-input">
          <option value="">All Roles</option>
          <option value="admin">Admin</option>
          <option value="user">User</option>
          <option value="viewer">Viewer</option>
        </select>
        <select v-model="statusFilter" class="glass-input">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="pending">Pending</option>
        </select>
      </div>
    </div>

    <!-- Users Table -->
    <div class="glass-panel overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="border-b border-gray-200 dark:border-white/10">
              <th class="text-left p-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                <input type="checkbox" class="rounded border-gray-300 dark:border-gray-600" />
              </th>
              <th class="text-left p-4 text-sm font-semibold text-gray-700 dark:text-gray-300">User</th>
              <th class="text-left p-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Role</th>
              <th class="text-left p-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Status</th>
              <th class="text-left p-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Last Active</th>
              <th class="text-left p-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Total Queries</th>
              <th class="text-left p-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Average Query Time</th>
              <th class="text-left p-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Query Cost</th>
              <th class="text-left p-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="user in filteredUsers" 
              :key="user.id"
              class="border-b border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
            >
              <td class="p-4">
                <input type="checkbox" class="rounded border-gray-300 dark:border-gray-600" />
              </td>
              <td class="p-4">
                <div class="flex items-center space-x-3">
                  <div class="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-semibold shadow-lg">
                    {{ user.initials }}
                  </div>
                  <div>
                    <p class="font-medium text-gray-900 dark:text-white">{{ user.name }}</p>
                    <p class="text-sm text-gray-500 dark:text-gray-400">{{ user.email }}</p>
                  </div>
                </div>
              </td>
              <td class="p-4">
                <span 
                  class="px-3 py-1 rounded-full text-xs font-medium"
                  :class="getRoleBadgeClass(user.role)"
                >
                  {{ user.role }}
                </span>
              </td>
              <td class="p-4">
                <span 
                  class="px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 w-fit"
                  :class="getStatusBadgeClass(user.status)"
                >
                  <span class="w-2 h-2 rounded-full" :class="getStatusDotClass(user.status)"></span>
                  <span>{{ user.status }}</span>
                </span>
              </td>
              <td class="p-4 text-sm text-gray-600 dark:text-gray-400">
                {{ user.lastActive }}
              </td>
              <td class="p-4 text-sm">
                <span class="font-bold" :class="getTotalQueriesBadgeClass(user.totalQueries)">
                  {{ user.totalQueries }}
                </span>
              </td>
              <td class="p-4 text-sm">
                <span class="font-bold" :class="getAverageQueryTimeBadgeClass(user.averageQueryTime)">
                  {{ formatQueryTime(user.averageQueryTime) }}
                </span>
              </td>
              <td class="p-4 text-sm">
                <span class="font-bold" :class="getQueryCostBadgeClass(user.queryCost)">
                  {{ formatQueryCost(user.queryCost) }}
                </span>
              </td>
              <td class="p-4">
                <div class="flex items-center space-x-2">
                  <button 
                    @click="editUser(user)"
                    class="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors" 
                    title="Edit"
                  >
                    <svg class="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button 
                    @click="deleteUser(user.id)"
                    class="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors" 
                    title="Delete"
                  >
                    <svg class="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                  <button class="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors" title="More">
                    <svg class="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="p-4 border-t border-gray-200 dark:border-white/10 flex items-center justify-between">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Showing <span class="font-medium">{{ filteredUsers.length }}</span> of <span class="font-medium">{{ users.length }}</span> users
        </p>
        <div class="flex items-center space-x-2">
          <button class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors">
            Previous
          </button>
          <button class="px-4 py-2 text-sm font-medium bg-gradient-to-r from-purple-600 to-blue-500 text-white rounded-lg shadow-lg shadow-purple-500/30">
            1
          </button>
          <button class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors">
            2
          </button>
          <button class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors">
            3
          </button>
          <button class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors">
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// Sample user data
const users = ref([
  {
    id: 1,
    name: 'Sarah Johnson',
    email: 'sarah.johnson@example.com',
    initials: 'SJ',
    role: 'admin',
    status: 'active',
    lastActive: '2 hours ago',
    totalQueries: 13,
    averageQueryTime: 2,
    queryCost: .13
  },
  {
    id: 2,
    name: 'Michael Chen',
    email: 'michael.chen@example.com',
    initials: 'MC',
    role: 'user',
    status: 'active',
    lastActive: '5 hours ago',
    totalQueries: 5,
    averageQueryTime: 43,
    queryCost: 1.13
  },
  {
    id: 3,
    name: 'Emily Rodriguez',
    email: 'emily.rodriguez@example.com',
    initials: 'ER',
    role: 'admin',
    status: 'active',
    lastActive: '1 day ago',
    totalQueries: 12,
    averageQueryTime: 12,
    queryCost: .72
  },
  {
    id: 4,
    name: 'David Kim',
    email: 'david.kim@example.com',
    initials: 'DK',
    role: 'user',
    status: 'inactive',
    lastActive: '3 days ago',
    totalQueries: 2,
    averageQueryTime: 345,
    queryCost: 3.45
  },
  {
    id: 5,
    name: 'Jessica Martinez',
    email: 'jessica.martinez@example.com',
    initials: 'JM',
    role: 'viewer',
    status: 'active',
    lastActive: '30 minutes ago',
    totalQueries: 1,
    averageQueryTime: 72,
    queryCost: .36
  },
  {
    id: 6,
    name: 'Robert Taylor',
    email: 'robert.taylor@example.com',
    initials: 'RT',
    role: 'user',
    status: 'pending',
    lastActive: 'Never',
    totalQueries: 0,
    averageQueryTime: 0,
    queryCost: 0
  },
  {
    id: 7,
    name: 'Amanda White',
    email: 'amanda.white@example.com',
    initials: 'AW',
    role: 'admin',
    status: 'active',
    lastActive: '1 hour ago',
    totalQueries: 32,
    averageQueryTime: 4,
    queryCost: .64
  },
  {
    id: 8,
    name: 'Christopher Lee',
    email: 'christopher.lee@example.com',
    initials: 'CL',
    role: 'user',
    status: 'active',
    lastActive: '4 hours ago',
    totalQueries: 1,
    averageQueryTime: 654,
    queryCost: 2.85
  }
])

// Stats
const stats = computed(() => ({
  totalUsers: users.value.length,
  activeUsers: users.value.filter(u => u.status === 'active').length,
  admins: users.value.filter(u => u.role === 'admin').length,
  pending: users.value.filter(u => u.status === 'pending').length
}))

// Filters
const searchQuery = ref('')
const roleFilter = ref('')
const statusFilter = ref('')

// Filtered users
const filteredUsers = computed(() => {
  return users.value.filter(user => {
    const matchesSearch = !searchQuery.value || 
      user.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      user.role.toLowerCase().includes(searchQuery.value.toLowerCase())
    
    const matchesRole = !roleFilter.value || user.role === roleFilter.value
    const matchesStatus = !statusFilter.value || user.status === statusFilter.value
    
    return matchesSearch && matchesRole && matchesStatus
  })
})

// Badge styling functions
const getRoleBadgeClass = (role: string) => {
  const classes = {
    admin: 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300',
    user: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300',
    viewer: 'bg-gray-100 dark:bg-gray-500/20 text-gray-700 dark:text-gray-300'
  }
  return classes[role as keyof typeof classes] || classes.user
}

const getStatusBadgeClass = (status: string) => {
  const classes = {
    active: 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300',
    inactive: 'bg-gray-100 dark:bg-gray-500/20 text-gray-700 dark:text-gray-300',
    pending: 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300'
  }
  return classes[status as keyof typeof classes] || classes.inactive
}

const getStatusDotClass = (status: string) => {
  const classes = {
    active: 'bg-green-500',
    inactive: 'bg-gray-500',
    pending: 'bg-orange-500'
  }
  return classes[status as keyof typeof classes] || classes.inactive
}

// Format query time from seconds to human-readable format
const formatQueryTime = (seconds: number): string => {
  if (seconds === 0) return '0s'
  
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  
  const parts: string[] = []
  
  if (hours > 0) {
    parts.push(`${hours}h`)
  }
  if (minutes > 0) {
    parts.push(`${minutes}m`)
  }
  if (secs > 0 || parts.length === 0) {
    parts.push(`${secs}s`)
  }
  
  return parts.join(' ')
}

// Get color class for Total Queries based on value
const getTotalQueriesBadgeClass = (totalQueries: number): string => {
  if (totalQueries < 30) {
    return 'text-green-600 dark:text-green-400'
  } else if (totalQueries >= 30 && totalQueries <= 50) {
    return 'text-yellow-600 dark:text-yellow-400'
  } else {
    return 'text-red-600 dark:text-red-400'
  }
}

// Get color class for Average Query Time based on seconds
const getAverageQueryTimeBadgeClass = (seconds: number): string => {
  if (seconds < 10) {
    return 'text-green-600 dark:text-green-400'
  } else if (seconds >= 10 && seconds <= 60) {
    return 'text-yellow-600 dark:text-yellow-400'
  } else {
    return 'text-red-600 dark:text-red-400'
  }
}

// Format query cost to USD currency
const formatQueryCost = (cost: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(cost)
}

// Get color class for Query Cost based on value
const getQueryCostBadgeClass = (cost: number): string => {
  if (cost < 2) {
    return 'text-green-600 dark:text-green-400'
  } else if (cost >= 2 && cost <= 3) {
    return 'text-yellow-600 dark:text-yellow-400'
  } else {
    return 'text-red-600 dark:text-red-400'
  }
}

// Action Handlers
const addUser = () => {
  const id = users.value.length + 1
  const newNames = ['Alex', 'Jordan', 'Taylor', 'Casey', 'Morgan']
  const index = Math.floor(Math.random() * newNames.length)
  const randomName = newNames[index] || 'User'
  
  users.value.unshift({
    id,
    name: `${randomName} New`,
    email: `${randomName.toLowerCase()}.new@example.com`,
    initials: `${randomName[0]}N`,
    role: 'user',
    status: 'pending',
    lastActive: 'Just now',
    totalQueries: 0,
    averageQueryTime: 0,
    queryCost: 0
  })
}

const deleteUser = (id: number) => {
  if (confirm('Are you sure you want to delete this user?')) {
    users.value = users.value.filter(u => u.id !== id)
  }
}

const editUser = (user: any) => {
  console.log('Edit user:', user)
  alert(`Editing user: ${user.name}`)
}
</script>
